import type { PlatformActionProposal } from '../models/platformAction.types'
import { adaptActionRequest } from '../service/adaptActionRequest'
import { classifyError } from '../service/classifyError'

import type { FlowResult, RevertActionApi } from './platformActionFlow.types'

export interface RevertActionFlowDeps {
  api: RevertActionApi
}

/**
 * Undo a succeeded request by proposing its inverse.
 *
 * This is NOT a rollback call: the response is propose's own shape, so what comes back is a fresh
 * `pending_approval` request plus a confirm token, and nothing changes on the platform until that
 * is approved through the ordinary path. Hosts must show it as an approval, never as a done deed.
 *
 * The returned `actionType` may be the SWAPPED verb rather than the original's — undoing a
 * `keyword.negative_add` comes back as a `keyword.negative_remove`. Read it off the response.
 */
export class RevertActionFlow {
  constructor(private readonly deps: RevertActionFlowDeps) {}

  async execute(
    workspaceId: number,
    actionId: string,
    signal?: AbortSignal,
  ): Promise<FlowResult<PlatformActionProposal>> {
    try {
      const result = await this.deps.api.revert(workspaceId, actionId, signal)
      return {
        ok: true,
        value: {
          request: adaptActionRequest(result.request),
          confirmToken: result.confirm_token,
        },
      }
    } catch (cause) {
      return { ok: false, error: classifyError(cause) }
    }
  }
}
